import { faCube } from "@jewlr/pro-light-svg-icons/faCube"
import { faPlusCircle } from "@jewlr/pro-light-svg-icons/faPlusCircle"
import { faSpinner } from "@jewlr/pro-regular-svg-icons/faSpinner"
import {
  Box,
  Button,
  Divider,
  Flex,
  FontAwesomeIcon,
  MediaBox,
} from "@jewlr/storybook/core"
import loadable from "@loadable/component"
import PropTypes from "prop-types"
import React, { useEffect, Suspense, useState } from "react"
import { connect } from "react-redux"
import styled from "styled-components"

import withImages from "areas/product/hoc/with-images"
import {
  toggleJ3DModal,
  toggleJ3DV2Modal,
  trackSlide,
} from "areas/product/store/actions"
import Slider from "dependencies/carousel"
import { isTestMode, retry } from "helpers/application"
import { isTouchDevice } from "helpers/browser"
import { isVideoView, isJ3dView } from "helpers/gallery-slide-view"
import { getPref } from "helpers/user-prefs"
import useWindowDimensions from "helpers/window-dimensions"

import { StyledImage } from "./components"
import MagnifyImage from "./magnify-image"
import ProductVideo from "./product-video"

const ZoomImgModal = loadable(() => retry(() => import("./zoom-img-modal")), {
  ssr: false,
})
const ProductJ3D = loadable(() => retry(() => import("./product-j3d")), {
  ssr: false,
})
const ProductJ3DV2 = loadable(() => retry(() => import("./product-j3d-v2")), {
  ssr: false,
})

const ImageSlider = styled(Slider)`
  padding-bottom: 23px;

  .slider-list {
    gap: 1px;
  }
  .slider-control-bottomcenter {
    margin-bottom: -24px;
  }
  .slider-control-bottomcenter button {
    padding: 0 3px;
  }
  .slider-control-bottomcenter button svg {
    height: 4px;
    width: 4px;
  }

  ${(props) => props.theme.mediaQueries.desktop`
    padding-bottom: 5px;

    .slider-control-bottomcenter {
      display: none;
    }
  `}
`

const SpinnerContainer = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  opacity: ${(props) => (props.show ? "1" : "0")};
  position: absolute;
  transition: opacity 0.3s;
  visibility: ${(props) => (props.show ? "visible" : "hidden")};
  width: 100%;
  z-index: 1;
`
const TangibleeBtn = styled(Button)`
  border-radius: 4px;
  bottom: 10px;
  display: none;
  font-size: 14px;
  left: 50%;
  padding: 16px 24px;
  position: absolute;
  transform: translateX(-50%);
  width: 90%;
  ${(props) => props.theme.mediaQueries.tablet`
    left: 0;
    width: 100%;
    transform: none;
  `}
`
const Arrow = styled(Button).attrs({
  variant: "text",
})`
  align-items: center;
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
  display: flex;
  height: 50px;
  justify-content: center;
  left: ${(props) => (props.left ? "0" : "calc(100% - 50px)")};
  position: absolute;
  right: ${(props) => (props.right ? "0" : "calc(100% - 50px)")};
  top: calc(50% - 25px);
  width: 50px;
  z-index: 1;
  ${(props) => props.theme.mediaQueries.tablet`
    top: calc(325px / 2 - 25px);
  `}
`

const ViewButton = styled(Button).attrs({
  variant: "text",
})`
  color: ${(props) => props.theme.colors.primary};
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  margin: 15px 0;
  text-decoration: underline;
`
const TangibleeContainer = styled(Box).attrs({
  id: "tangiblee-cta",
})`
  display: flex;
  &:empty,
  .tng-cta-wrapper:empty,
  &:has(div:last-child:empty) {
    display: none;
  }
  &:not(:empty):not(:first-child)::before {
    border-left: 1px solid #d9d9d9;
    content: "";
    height: 18px;
    margin: auto 0;
    padding-left: 18px;
  }
  ${(props) => props.theme.mediaQueries.desktop`
    &:empty:only-child {
      display: block;
      height: 46px;
    }
  `}
`
const ActionBtnRow = styled(Flex).attrs((props) => ({
  alignItems: "center",
  borderTop: { _: "none", desktop: `1px solid ${props.theme.colors.grey10}` },
  gap: "16px",
  jusutifyContent: "center",
  mx: "auto",
  pt: { _: 0, desktop: "5px" },
  width: "fit-content",
}))``

/**
 * Main product image slider
 * @param  {object} blur - from withImages
 * @param  {array} slidesArray - from withImages
 * @param  {bool} wizardLoading - from withImages
 */
const InternalMainImageSlider = ({
  blur,
  currentSlideIndex,
  dataCyType,
  defaultImage,
  displayMiniview,
  j3dSlideIndex,
  miniview,
  openJ3DModal,
  openJ3DV2Modal,
  productTitle,
  setBlur,
  showJ3d,
  showJ3dV2,
  slidesArray,
  styleCode,
  trackSlide,
  wizardLoading,
}) => {
  // const [showSrc, setShowSrc] = useState(false)
  const [zoomImg, setZoomImg] = useState()
  const [enableJ3D, setEnableJ3D] = useState(false)
  const { isTablet } = useWindowDimensions()

  const swipeable = miniview
    ? true
    : sl.config.enable_j3d_carousel
    ? j3dSlideIndex > 0 && slidesArray.length > 1
      ? currentSlideIndex < slidesArray.length - 1
      : true
    : true

  const sliderSettings = {
    arrows: false,
    beforeSlide: (_, index) => {
      if (!miniview || displayMiniview) {
        trackSlide(index)
      }
    },
    dragging: swipeable,
    frameAriaLabel: "Product Images",
    slideIndex: miniview
      ? Math.min(
          slidesArray.filter((s) => !isVideoView(s.view) && !isJ3dView(s.view))
            .length - 1,
          currentSlideIndex
        )
      : currentSlideIndex,
    speed: 300,
    swiping: swipeable,
    tabbed: isTablet,
    wrapAround: false,
  }

  const showZoomBtn =
    !isVideoView(slidesArray[currentSlideIndex]?.view) &&
    !isJ3dView(slidesArray[currentSlideIndex]?.view) &&
    !wizardLoading
  const showMobileZoomModal =
    (typeof window !== "undefined" && isTouchDevice()) || isTablet

  useEffect(() => {
    if (!miniview && currentSlideIndex === j3dSlideIndex && !enableJ3D) {
      setEnableJ3D(true)
    }
  }, [currentSlideIndex])

  useEffect(() => {
    setZoomImg(null)
  }, [currentSlideIndex, showMobileZoomModal, wizardLoading, styleCode])

  useEffect(() => {
    return () => {
      trackSlide(0)
    }
  }, [])

  const doViewIn3D = () => {
    if (
      sl.config.enable_j3d_carousel &&
      (showJ3dV2 || showJ3d) &&
      j3dSlideIndex >= 0
    ) {
      trackSlide(j3dSlideIndex)
    } else if (showJ3dV2) {
      openJ3DV2Modal(true)
    } else if (showJ3d) {
      openJ3DModal(true)
    }
    if (!isTestMode) {
      dataLayer.push({
        event: "viewed-j3d",
        styleCode,
      })
    }
  }

  return (
    <Flex
      flexDirection="column"
      minHeight={{
        _: miniview ? "unset" : "calc(100vw - 16px)",
        desktop: "500px",
        tablet: "325px",
      }}
      position="relative"
      px={{ _: "12px", tablet: 0 }}
    >
      <SpinnerContainer
        data-cy={
          wizardLoading
            ? "product-main-slider-main-img-spinning"
            : "product-main-slider-main-img-not-spinning"
        }
        show={wizardLoading || blur}
      >
        <FontAwesomeIcon icon={faSpinner} size="2x" spin />
      </SpinnerContainer>
      {/* Temporary Arrow Control for J3D */}
      {sl.config.enable_j3d_carousel &&
        j3dSlideIndex > 0 &&
        !miniview &&
        currentSlideIndex === slidesArray.length - 1 && (
          <MediaBox
            height="100%"
            left={0}
            lessThan="desktop"
            minHeight={{
              tablet: "calc(50vw - 16px)",
            }}
            position="absolute"
            top={0}
            width="100%"
          >
            <Arrow
              left={true}
              onClick={() => {
                trackSlide(currentSlideIndex - 1)
              }}
            >
              <StyledImage
                alt={productTitle}
                className="lazyload"
                data-src={require("images/icons/arrow_left.svg")}
                marginRight={3}
                width="15px"
              />
            </Arrow>
          </MediaBox>
        )}
      {/* End of Temporary Arrow Control for J3D */}

      <Box position="relative">
        <ImageSlider {...sliderSettings}>
          {!defaultImage && slidesArray.length == 0 && (
            <Box position="relative">
              <StyledImage
                alt="Loading"
                blur
                ratio="325 / 325"
                src={require("images/icons/jewlr.gif")}
              />
            </Box>
          )}
          {slidesArray
            .filter((slide) =>
              miniview
                ? !isVideoView(slide.view) && !isJ3dView(slide.view)
                : true
            )
            .map((slide, index) => (
              <Box
                aria-labelledby={`image-slider-tab-${index + 1}`}
                height="100%"
                id={`image-slider-tabpanel-${index + 1}-${
                  miniview ? "miniview" : "main"
                }`}
                isImg={!isVideoView(slide.view) && !isJ3dView(slide.view)}
                key={`mainslider-${slide.view}`}
                role="tabpanel"
                tabIndex={currentSlideIndex === index ? "0" : "-1"}
                width="100%"
              >
                {isVideoView(slide.view) ? (
                  !miniview ? (
                    <ProductVideo
                      video={slide.src}
                      {...{ currentSlideIndex, index }}
                    />
                  ) : null
                ) : isJ3dView(slide.view) ? (
                  !miniview ? (
                    <Box
                      height="100%"
                      maxHeight={{
                        _: "auto",
                        desktop: "500px",
                        tablet: "325px",
                      }}
                      maxWidth={{
                        _: "auto",
                        desktop: "500px",
                        tablet: "325px",
                      }}
                      mx="auto"
                      width="100%"
                    >
                      {typeof window !== "undefined" && enableJ3D && (
                        <Suspense fallback={<div />}>
                          {showJ3d && !showJ3dV2 && <ProductJ3D />}
                          {showJ3dV2 && <ProductJ3DV2 />}
                        </Suspense>
                      )}
                    </Box>
                  ) : null
                ) : (
                  <Box position="relative">
                    {sl.config.enable_pdp_image_zoom ? (
                      <MagnifyImage
                        alt={productTitle}
                        blur={blur || wizardLoading}
                        className={`prod-img-${slide.view}`}
                        data-cy={`product-main-slider-main-img-${index}${
                          dataCyType ? `-${dataCyType}` : ""
                        }`}
                        lazyload={index > 0}
                        mgShowZoom={
                          sl.config.enable_pdp_image_zoom &&
                          currentSlideIndex === index &&
                          zoomImg &&
                          !isVideoView(slide.view) &&
                          !isJ3dView(slide.view)
                        }
                        onLoad={() => {
                          if (index == 0) {
                            // setShowSrc(true)
                            setBlur(false)
                          }
                        }}
                        ratio="325 / 325"
                        sizes={slide.sizes}
                        src={slide.src}
                        srcSet={slide.srcset}
                      />
                    ) : (
                      <StyledImage
                        alt={productTitle}
                        blur={blur || wizardLoading}
                        className={`prod-img-${slide.view}`}
                        data-cy={`product-main-slider-main-img-${index}${
                          dataCyType ? `-${dataCyType}` : ""
                        }`}
                        lazyload={index > 0}
                        onLoad={() => {
                          if (index == 0) {
                            // setShowSrc(true)
                            setBlur(false)
                          }
                        }}
                        ratio="325 / 325"
                        sizes={slide.sizes}
                        src={slide.src}
                        srcSet={slide.srcset}
                      />
                    )}

                    {!miniview && slide.view === "tangiblee_view" && (
                      <TangibleeBtn
                        className="js-tangiblee-cta"
                        variant="primary"
                      >
                        TAP TO VIEW SIZE
                      </TangibleeBtn>
                    )}
                  </Box>
                )}
              </Box>
            ))}
        </ImageSlider>
      </Box>
      {!miniview && (
        <ActionBtnRow>
          {(showJ3d || showJ3dV2) && (
            <ViewButton onClick={() => doViewIn3D()}>
              <FontAwesomeIcon
                color="primary"
                fontSize="13px"
                icon={faCube}
                mr="6px"
              />
              View in 3D
            </ViewButton>
          )}
          {sl.config.enable_pdp_image_zoom &&
            typeof window !== "undefined" &&
            showZoomBtn && (
              <Suspense fallback={<div></div>}>
                <>
                  {(showJ3d || showJ3dV2) && (
                    <Box bg="#d9d9d9" height="18px" width="1px" />
                  )}
                  <ViewButton
                    className="fs-ignore-rage-clicks"
                    onClick={() => {
                      setZoomImg(
                        zoomImg ? null : slidesArray[currentSlideIndex]
                      )
                    }}
                  >
                    <FontAwesomeIcon
                      color="primary"
                      fontSize="14px"
                      icon={faPlusCircle}
                      mr="6px"
                    />
                    {isTablet ? "Tap to Zoom" : zoomImg ? "Exit Zoom" : "Zoom"}
                  </ViewButton>
                </>
              </Suspense>
            )}
          <TangibleeContainer></TangibleeContainer>
        </ActionBtnRow>
      )}

      {!miniview && (
        <Divider display={{ tablet: "none" }} mb="12px" mt="5px" mx={3} />
      )}

      {sl.config.enable_pdp_image_zoom && showMobileZoomModal && (
        <Suspense fallback={<div></div>}>
          <ZoomImgModal
            onClose={() => setZoomImg(null)}
            productTitle={productTitle}
            zoomImg={zoomImg}
          />
        </Suspense>
      )}
    </Flex>
  )
}

InternalMainImageSlider.propTypes = {
  blur: PropTypes.bool,
  currentSlideIndex: PropTypes.number,
  dataCyType: PropTypes.string,
  defaultImage: PropTypes.object,
  displayMiniview: PropTypes.bool,
  j3dSlideIndex: PropTypes.number,
  miniview: PropTypes.bool,
  openJ3DModal: PropTypes.func,
  openJ3DV2Modal: PropTypes.func,
  productTitle: PropTypes.string,
  setBlur: PropTypes.func,
  showJ3d: PropTypes.bool,
  showJ3dV2: PropTypes.bool,
  slidesArray: PropTypes.array,
  styleCode: PropTypes.string,
  trackSlide: PropTypes.func,
  wizardLoading: PropTypes.string,
}

const getMiniview = (state) => {
  return (
    state.previewImageUI.miniview.show &&
    !state.previewImageUI.miniview.swipeHide &&
    !state.previewImageUI.miniview.scrollHide &&
    !getPref("hide_miniview")
  )
}

const mapStateToProps = (state) => ({
  currentSlideIndex: state.previewImage.slideIndex,
  displayMiniview: getMiniview(state),
  productTitle: state.wizard.title,
  showJ3d: state.previewImage.j3d,
  showJ3dV2: state.previewImage.j3dV2,
})

const mapDispatchToProps = (dispatch) => {
  return {
    openJ3DModal: () => {
      dispatch(toggleJ3DModal(true))
    },
    openJ3DV2Modal: () => {
      dispatch(toggleJ3DV2Modal(true))
    },
    trackSlide: (index) => {
      dispatch(trackSlide(index))
    },
  }
}

const MainImageSlider = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalMainImageSlider)

export default withImages(MainImageSlider)
